import { z } from "zod";
import { artifactPort, collectionPort } from "@loumy/contracts";

// §6 Capability-Port-Schemas — benannte Artefakt-Ports (kind-gepinnt, introspektierbar
// für den Build-Kind-Check) + schema-validierte Signal-/Scalar-Felder (D38/D41/D42).
// Signal-Ports (verdict/score/needsChromaKey/keyColor/loopScore) tragen KEINE Kind und
// werden vom Kind-Check übersprungen (sound).

/** json-Signal (schema-validiertes Verdict-/Score-Feld, §3.2/§6). */
const jsonSignal = z.record(z.string(), z.unknown());

// input-validate (VLM-Gate, D50/D52) ──────────────────────────────────────────
export const InputValidateInput = z.object({ image: artifactPort("image") });
export const InputValidateOutput = z.object({
  verdict: z.enum(["pass", "edit", "reject"]),
  score: jsonSignal,
  // D79: empfohlene Screen-Farbe (kontrastierend zum Motiv) — Signal, das enhance/normalize/key
  // konsumieren (Pixverse rendert sie, chroma-key entfernt sie). Default green (graziöse Degr.).
  keyColor: z.enum(["green", "blue"]),
  // D114: VLM-Begründung des Urteils als eigener Port — normalize liest ihn via ScalarRef, damit
  // schon die ERSTE Edit-Runde weiß, WAS beanstandet wurde (vorher blind: reason startete undefined,
  // erst Runde 2 kannte den Re-Validate-Grund). Leerstring wenn das VLM keinen Grund lieferte.
  reason: z.string(),
});

// input-normalize (Image-Edit-Schleife → loop-taugliche/prompt-treue Pose, D50/D76) ─────────
export const InputNormalizeInput = z.object({ image: artifactPort("image") });
export const InputNormalizeOutput = z.object({
  image: artifactPort("image"),
  // D76-Signale: `prepared`=true wenn die Re-Validate-Schleife (max N Edits) ein 'pass' erreichte.
  // `finalVerdict` = letztes VLM-Urteil. Der Runner gated auf prepared=false (Job-Fail + release,
  // Bild bleibt als prepared-input erhalten → „Trotzdem generieren"). Signale, keine Artefakte.
  prepared: z.boolean(),
  finalVerdict: z.enum(["pass", "edit", "reject"]),
});

// prompt-enhance ───────────────────────────────────────────────────────────────
// D80: optionale Referenzbilder (image-generator-Edit-Modus, §10.2) — dasselbe Upload, das auch
// image-gen erhält. Sind sie gesetzt, reicht prompt-enhance das Bild an den (multimodalen) LLM
// durch, damit dieser den Stil SIEHT und bewahrt (kein „hyperrealistic"-Drift). Optional ⇒
// text-to-image und spritesheet(motion) bleiben unberührt.
export const PromptEnhanceInput = z.object({
  reference1: artifactPort("image").optional(),
  reference2: artifactPort("image").optional(),
  // D127/D129: optionales Motiv-Bild (spritesheet: der ROHE Start-Frame) — geht in den HAUPT-
  // Call (D129: kein Extra-Titel-Call mehr), dient aber NUR der Titel-Subjekt-Treue: der LLM
  // SIEHT das Subjekt und benennt es in der TITLE-Erstzeile, statt aus einem knappen Motion-
  // Prompt („walking") eines zu halluzinieren („Dog Walking Service", identisch je Prompt).
  // Optional ⇒ Alt-Specs (enhance ohne inputs) und text-to-image bleiben unberührt.
  subject: artifactPort("image").optional(),
});
export const PromptEnhanceOutput = z.object({
  prompt: jsonSignal, // veredelter Prompt (json)
  needsChromaKey: z.boolean(), // Transparenz-Signal (§10.2)
  // D97: die vom ENHANCER gewählte Screen-Farbe (image-generator-Flow) — Kontrastregel: viel
  // Grün am Subjekt → blue, viel Blau → green, sonst green. Der key-Knoten konsumiert sie als
  // ScalarRef (gleiche Mechanik wie validate.keyColor, D79). Bei mode='motion' (spritesheet)
  // Echo des D79-Gate-Werts; bei needsChromaKey=false inert (Default green).
  keyColor: z.enum(["green", "blue"]),
  // D98: kurzer Job-Titel (Signal) — vom LLM vergeben (gleiche Sprache wie der Prompt), mit
  // deterministischer Degradierung (gekürzter Roh-Prompt). Erreicht die Jobs-Liste über die
  // persistierten Signale (executed_pipeline_spec.signals.enhance.title, wie pack.meta/D68).
  title: z.string(),
});

// video-gen (VideoProvider) ─────────────────────────────────────────────────────
// Optionaler endFrame → Transition-Endpoint. D107 (= D76 restauriert): loop=on ⇒ der Build
// setzt endFrame=startFrame (first==last-FLF — der Provider schließt den Loop); loop=off mit
// End-Frame ⇒ gerichtete Transition (D91). Fehlt er → i2v.
export const VideoGenInput = z.object({
  startFrame: artifactPort("image"),
  endFrame: artifactPort("image").optional(),
});
export const VideoGenOutput = z.object({
  video: artifactPort("video"),
  audio: artifactPort("audio").optional(), // eigener Port (§6)
});

// image-gen (ImageProvider, Collection-Output) ─────────────────────────────────
export const ImageGenInput = z.object({
  reference1: artifactPort("image").optional(),
  reference2: artifactPort("image").optional(),
});
export const ImageGenOutput = z.object({ images: collectionPort("image") });

// chroma-key (Media-Union, besitzt Decode; spiegelt Modalität, D38) ─────────────
// Varianten .strict(): fail-closed disjunkte Union (N3) — ein Input/Output mit
// mehreren Modalitäts-Keys (z.B. {image, video}) wird ABGELEHNT statt still gestript.
// Der innere artifactPort bleibt .passthrough() (Laufzeit-Handle-Felder, M5).
export const ChromaKeyInput = z.union([
  z.object({ image: artifactPort("image") }).strict(),
  z.object({ video: artifactPort("video") }).strict(),
  z.object({ frames: artifactPort("frames") }).strict(),
]);
export const ChromaKeyOutput = z.union([
  z.object({ image: artifactPort("image") }).strict(), // image → image
  z.object({ frames: artifactPort("frames") }).strict(), // video/frames → frames
]);

// loop-fix (Pose+Optical-Flow-Crop, D49 L2) — DEPRECATED (D94/D32): kein Tool baut den
// Knoten mehr; war durch `loop` (D94) ersetzt, das per D107 SELBST deprecated ist
// (loop=on = first==last-FLF-Transition, D76 restauriert). ─────────────────────────────
export const LoopFixInput = z.object({ frames: artifactPort("frames") });
export const LoopFixOutput = z.object({
  frames: artifactPort("frames"),
  loopScore: jsonSignal, // persistierter Score speist „Fix Loop" (D49 L3)
});

// loop (D94: Loop-Cut + FILM-Naht) — D108: wieder AKTIV als Cut-only-Knoten (ähnlichster-
// Frame-Schnitt auf dem FLF-Video, OHNE FILM-Pin); Alt-Specs MIT Pin = D94-Naht-Pfad (D32). ──
// Nimmt das ROHE gen-Video (vor chroma-key): decode → Cut beim startähnlichsten Frame
// (alles dahinter weg) → FILM (fal.ai) interpoliert 1–2 Naht-Frames zwischen neuem
// End-Frame und Start-Frame (nach seamSimilarity) → Append. Output sind ROH-Frames
// (Greenscreen erhalten) — der chroma-key-Knoten keyt die Sequenz danach EINHEITLICH.
export const LoopInput = z.object({ video: artifactPort("video") });
export const LoopOutput = z.object({
  frames: artifactPort("frames"),
  loopScore: jsonSignal, // bestIndex/score/seamSimilarity/seamFrames — persistiertes Signal (D49 L3)
});

// transparency-crop (Union-BBox) ───────────────────────────────────────────────
export const TransparencyCropInput = z.object({ frames: artifactPort("frames") });
export const TransparencyCropOutput = z.object({ frames: artifactPort("frames") });

// frames-select-segments (D139 — Studio-Timeline-Schnitt, SSOT) ─────────────────
// Media-Union wie chroma-key: video → decode → frames → Segment-Auswahl; frames → Auswahl.
// Der WebM-Export-Job (studio-export) schneidet damit IDENTISCH zur Pyodide-Browser-Vorschau.
export const FramesSelectSegmentsInput = z.union([
  z.object({ video: artifactPort("video") }).strict(),
  z.object({ frames: artifactPort("frames") }).strict(),
]);
export const FramesSelectSegmentsOutput = z.object({ frames: artifactPort("frames") });

// frame-interpolate (RIFE → targetFrameCount, D49) ─────────────────────────────
export const FrameInterpolateInput = z.object({ frames: artifactPort("frames") });
export const FrameInterpolateOutput = z.object({ frames: artifactPort("frames") });

// frames-resize (D168: Downscale-back nach dem SeedVR2-2×-Pass — Sheet bleibt quellgroß) ──
export const FramesResizeInput = z.object({ frames: artifactPort("frames") });
export const FramesResizeOutput = z.object({ frames: artifactPort("frames") });

// frame-smooth (D151: Dedup naher Duplikat-Frames + RIFE-Reinterpolation auf 24 fps) ──
// Front-Knoten direkt nach gen: nimmt das ROHE gen-Video, verwirft das Pixverse-„on twos"-
// Doubling (konsekutive Beinahe-Duplikate, SSIM) und re-densifiziert die Lücken via echtem
// RIFE (fal-ai/rife, paarweise) zurück auf flüssige 24 fps → gibt ein VIDEO aus (Drop-in für
// gen.video, damit loop/key unverändert video-basiert bleiben). Ist nichts zu deduplizieren
// (3D-Look, 49/49 unique) ODER fehlt der RIFE-Provider, wird das Roh-Video durchgereicht.
export const FrameSmoothInput = z.object({ video: artifactPort("video") });
// ARTEFAKT-ONLY (KEIN Signal-Port): smooth läuft VOR dem awaiting loop-Knoten (D27-Resume). Ein
// zusätzliches Signal würde den Knoten beim Resume über die Signal-Heuristik (runner.ts) als
// „done mit leeren Artefakten" rehydrieren, falls das Video-Artefakt (noch) nicht im Store steht
// ⇒ der loop-Knoten fände `smooth.video` nicht. Artefakt-only verhält es sich wie `gen`
// (rehydriert via persistiertem Artefakt bzw. läuft sonst sauber neu). Die Dedup-Statistik
// (removed/rifeFrames) ist damit kein persistiertes Signal (Observability = §13).
export const FrameSmoothOutput = z.object({ video: artifactPort("video") });

// video-encode (D132: RGBA-Frames → transparentes WebM, VP9-Alpha) ─────────────
export const VideoEncodeInput = z.object({ frames: artifactPort("frames") });
export const VideoEncodeOutput = z.object({ video: artifactPort("video") });

// video-enhance (D168: temporal-konsistente Video-Super-Resolution via SeedVR2) ──
// Optionaler Qualitäts-Knoten — rekonstruiert Detail/Schärfe bei hochskalierter Auflösung
// (SeedVR2 gewinnt gerade bei kurzen, KI-generierten, komprimierten Clips). Media-Union wie
// video-bg-remove (D146): video → SeedVR2 direkt (loop=off-/Seamless-off-Pfad); frames
// (Spritesheet loop=on — User-Direktive nach Live-Befund Job 2dbf3fbe: enhance NACH der
// VACE-Naht, die Naht rechnet auf Quellgröße und ihre Frames werden MITgeschärft) →
// frames-to-mp4 (Capability-intern). Gibt ein VIDEO aus; key liest es video-basiert.
// ARTEFAKT-ONLY wie frame-smooth (ein Signal-Port würde die Resume-Heuristik vergiften —
// s. FrameSmoothOutput-Begründung).
export const VideoEnhanceInput = z.union([
  z.object({ video: artifactPort("video") }).strict(),
  z.object({ frames: artifactPort("frames") }).strict(),
]);
export const VideoEnhanceOutput = z.object({ video: artifactPort("video") });

// video-bg-remove (D146: AI-Matting via BEN2 — DROP-IN für chroma-key, liefert Frames) ──
// Media-Union wie chroma-key (video → BEN2 direkt; frames → frames-to-mp4 → BEN2). KEIN
// image-Zweig: AI-Matting auf Einzelbildern ist das eigene bg-remove-Tool (image-bg-remove).
export const VideoBgRemoveInput = z.union([
  z.object({ video: artifactPort("video") }).strict(),
  z.object({ frames: artifactPort("frames") }).strict(),
]);
export const VideoBgRemoveOutput = z.object({ frames: artifactPort("frames") });

// image-bg-remove (D146: AI-Matting via Bria RMBG 2.0 — eigenständiges bg-remove-Tool) ──
export const ImageBgRemoveInput = z.object({ image: artifactPort("image") });
export const ImageBgRemoveOutput = z.object({ image: artifactPort("image") });

// image-upscale (D163: Super-Resolution via Real-ESRGAN — eigenständiges image-upscale-Tool) ──
export const ImageUpscaleInput = z.object({ image: artifactPort("image") });
export const ImageUpscaleOutput = z.object({ image: artifactPort("image") });

// spritesheet-pack ──────────────────────────────────────────────────────────────
export const SpritesheetPackInput = z.object({ frames: artifactPort("frames") });
export const SpritesheetPackOutput = z.object({
  sheet: artifactPort("spritesheet"),
  // Grid-Verdict (cols/rows/frameCount/frameWidth/frameHeight/fps/loop) — persistiertes
  // SIGNAL (D51/D68, wie loopScore), nicht Artefakt: erreicht so das Frontend über
  // `executed_pipeline_spec.signals.pack.meta` und speist den SpriteSheetPlayer (§10.1).
  meta: jsonSignal,
});
